import { type FC, Fragment } from 'react';

import SidebarHelpLink from '@/app/help-centre/components/SidebarHelpLink';
import { useAppMenu } from '@/hooks/menu';

import ImageComponent from '../../components/image';
import { MenuCollapseSVG, MenuExpandSVG } from '../../components/reusable/svgs';
import { pageurl } from '../../constants/pageurl';
import type { IMenuItem } from '../utils';
import { MenuItem } from './components/items';
import { SidebarElectionChip } from './components/SidebarElectionChip';
import { SidebarMenuSkeleton } from './components/SidebarMenuSkeleton';
import { UserProfileCard } from './components/UserProfileCard';

const SideMenu: FC<{ menu?: IMenuItem[] }> = () => {
	const {
		logoutUser,
		isCollapsed,
		navigate,
		organization,
		toggleSidebar,
		groupedMenu,
		snapshot,
		isPermissionsLoading,
		hasElection,
		openElectionPicker,
	} = useAppMenu();

	return (
		<div className={`side-menu-container ${isCollapsed ? 'toggle' : ''}`}>
			<nav>
				<div className='sidebar-brand'>
					<div
						className='sidebar-brand__logo'
						onClick={() => navigate(pageurl.OVERVIEW)}
					>
						<ImageComponent
							src={organization.logo}
							alt={organization.shortName || 'iElect'}
							className='sidebar-brand__img'
						/>
					</div>
					<div className='sidebar-brand__text'>
						<span className='sidebar-brand__name'>{organization.shortName || 'iElect'}</span>
						<span className='sidebar-brand__plan'>ERMS</span>
					</div>
					<button
						className='sidebar-brand__toggle'
						onClick={toggleSidebar}
						aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
					>
						{isCollapsed ? <MenuExpandSVG /> : <MenuCollapseSVG />}
					</button>
				</div>

				{!organization.isObserver && (
					<div className='sidebar-election-chip-slot'>
						<SidebarElectionChip collapsed={isCollapsed} />
					</div>
				)}

				<div className='sidebar-menu-list'>
					{isPermissionsLoading ? (
						<SidebarMenuSkeleton />
					) : (
						<ul className='m-0 p-0'>
							{groupedMenu.map((group) => (
								<Fragment key={group.section || '_top'}>
									{group.section && (
										<li className='menu-section'>
											<span>{group.section}</span>
										</li>
									)}
									{group.items.map((menuItem, index) => (
										<MenuItem
											icon={menuItem.icon}
											title={menuItem.title}
											isActive={menuItem.isActive}
											key={`${group.section}-${menuItem.title}-${index}`}
											pageUrl={menuItem.pageUrl}
											sub={menuItem.sub}
											roleActions={menuItem.roleActions}
											requiresElection={menuItem.requiresElection}
											snapshot={snapshot}
											hasElection={hasElection}
											onRequestElection={openElectionPicker}
										/>
									))}
								</Fragment>
							))}
						</ul>
					)}
				</div>

				<div className='bottom-section'>
					<SidebarHelpLink />
					<UserProfileCard onLogout={logoutUser} />
				</div>
			</nav>
		</div>
	);
};

export default SideMenu;
